import React, { useState, useEffect } from 'react';
import { Plus, Search, Users, UserCircle, MapPin, FileSpreadsheet, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import EmployeeModal from '../components/EmployeeModal';
import ExcelUploadModal from '../components/ExcelUploadModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function EmployeeBadge({ employee }) {
  // Colore badge basato su sesso
  const getBadgeColor = () => {
    if (employee.sesso === 'M') {
      return 'from-blue-400 to-blue-600'; // Azzurro per maschi
    } else if (employee.sesso === 'F') {
      return 'from-pink-400 to-pink-600'; // Rosa per donne
    }
    return 'from-primary/10 to-primary/5'; // Default
  };
  
  const getAvatarColor = () => {
    if (employee.sesso === 'M') {
      return 'bg-blue-500'; // Azzurro per maschi
    } else if (employee.sesso === 'F') {
      return 'bg-pink-500'; // Rosa per donne
    }
    return 'bg-primary'; // Default
  };
  
  return (
    <Link
      to={`/employees/${employee._id}`}
      className="group"
      data-testid="employee-badge"
    >
      <div className="bg-white rounded-xl border-2 border-border hover:border-primary hover:shadow-xl transition-all duration-300 overflow-hidden">
        {/* Header with gradient based on gender */}
        <div className={`h-24 bg-gradient-to-br ${getBadgeColor()} relative`}>
          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
            {employee.foto_url ? (
              <img
                src={`${API_URL}${employee.foto_url}`}
                alt={`${employee.nome} ${employee.cognome}`}
                className="w-24 h-24 rounded-full border-4 border-white object-cover shadow-lg"
              />
            ) : (
              <div className={`w-24 h-24 rounded-full border-4 border-white ${getAvatarColor()} text-white flex items-center justify-center text-2xl font-bold shadow-lg`}>
                {employee.nome?.charAt(0)}{employee.cognome?.charAt(0)}
              </div>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="pt-16 pb-6 px-6 text-center">
          <h3 className="text-xl font-bold mb-1 group-hover:text-primary transition-colors">
            {employee.nome} {employee.cognome}
          </h3>
          
          {employee.numero_matricola && (
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted rounded-full mb-3">
              <UserCircle size={16} className="text-muted-foreground" />
              <span className="text-sm font-mono font-semibold text-muted-foreground">
                {employee.numero_matricola}
              </span>
            </div>
          )}
          
          {employee.ruolo && (
            <p className="text-muted-foreground mb-2 font-medium">{employee.ruolo}</p>
          )}
          
          <div className="space-y-1 text-sm text-muted-foreground">
            {employee.sede && (
              <p className="flex items-center justify-center gap-1">
                <MapPin size={14} className="text-primary" />
                {employee.sede}
              </p>
            )}
            {employee.email && (
              <p className="truncate">📧 {employee.email}</p>
            )}
            {employee.telefono && (
              <p>📞 {employee.telefono}</p>
            )}
            {employee.data_assunzione && (
              <p className="text-xs mt-2">
                Assunto il {format(new Date(employee.data_assunzione), 'dd/MM/yyyy', { locale: it })}
              </p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  useEffect(() => {
    checkUserAccess();
  }, []);
  
  useEffect(() => {
    if (currentUser && !accessDenied) {
      fetchEmployees();
    }
  }, [search, currentUser, accessDenied]);
  
  const checkUserAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        navigate('/login');
        return;
      }
      
      const userData = await res.json();
      setCurrentUser(userData);
      
      // Only Admin and HSE Manager can see all employees
      if (userData.role === 'employee') {
        // Redirect employee to their own profile
        if (userData.employee_id) {
          navigate(`/employees/${userData.employee_id}`);
        } else {
          setAccessDenied(true);
        }
      }
    } catch (err) {
      console.error('Error checking access:', err);
      navigate('/login');
    }
  };
  
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const url = search 
        ? `${API_URL}/api/employees?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/employees`;
      
      const res = await fetch(url);
      const data = await res.json();
      setEmployees(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setLoading(false);
    }
  };
  
  // Check if user can manage (create/edit)
  const canManage = currentUser && ['admin', 'hse_manager'].includes(currentUser.role);
  
  const handleCreateEmployee = () => {
    setEditingEmployee(null);
    setShowModal(true);
  };
  
  const handleModalClose = (shouldRefresh) => {
    setShowModal(false);
    setEditingEmployee(null);
    if (shouldRefresh) {
      fetchEmployees();
    }
  };
  
  const handleExcelModalClose = (shouldRefresh) => {
    setShowExcelModal(false);
    if (shouldRefresh) {
      fetchEmployees();
    }
  };
  
  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Lock size={64} className="text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Accesso Limitato</h1>
        <p className="text-muted-foreground mb-4">
          Non hai i permessi per visualizzare l'elenco dei dipendenti.
        </p>
        <Link to="/profile" className="btn btn-primary">
          Vai al Mio Profilo
        </Link>
      </div>
    );
  }
  
  if (loading && employees.length === 0) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento dipendenti...</p>
      </div>
    );
  }
  
  return (
    <div data-testid="employees-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Dipendenti</h1>
            <p className="page-description">
              Gestisci l'anagrafica dei dipendenti
            </p>
          </div>
          {canManage && (
            <div className="flex items-center gap-3">
              <button 
                className="btn btn-secondary bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                onClick={() => setShowExcelModal(true)}
                data-testid="import-excel-button"
              >
                <FileSpreadsheet size={20} />
                Importa da Excel
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleCreateEmployee}
              data-testid="create-employee-button"
            >
              <Plus size={20} />
              Nuovo Dipendente
            </button>
          </div>
          )}
        </div>
      </div>
      
      {/* Search */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            className="form-input pl-10"
            placeholder="Cerca per nome, cognome, matricola o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
          />
        </div>
      </div>
      
      {/* Employee Badges Grid */}
      {employees.length === 0 ? (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <p>Nessun dipendente trovato</p>
          <button className="btn btn-primary mt-4" onClick={handleCreateEmployee}>
            <Plus size={20} />
            Aggiungi Primo Dipendente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {employees.map((employee) => (
            <EmployeeBadge key={employee._id} employee={employee} />
          ))}
        </div>
      )}
      
      {showModal && (
        <EmployeeModal
          employee={editingEmployee}
          onClose={handleModalClose}
        />
      )}
      
      {showExcelModal && (
        <ExcelUploadModal
          onClose={handleExcelModalClose}
        />
      )}
    </div>
  );
}

export default Employees;
